/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes */
import EmberObject, { computed, set } from '@ember/object';

import OpAmpValidation from './op-amp-validation';
import * as CommonUtils from '../../utils/common';

export default class OpAmpSelection extends EmberObject {
  data = null;
  limits = null;
  marginalLimits = null;
  componentsInput = null;
  gbw = null;

  partNumber = null;
  opAmpStatus = null;
  packageCode = null;
  vNoise = null;
  iNoise = null;
  minStableGain = null;
  vintage = null;
  vos = null;
  spiceModels = null;
  polesZeroes = null;

  opAmpRecommended = null;
  opAmpMarginal = null;
  opAmpMarginalHasNumberChannelsError = null;
  opAmpDisabled = null;
  init() {
    if (!!this.data && this.gbw === null) {
      set(this, 'gbw', Number(this.data.gbw));
    }

    if (!CommonUtils.isNullOrUndefined(this.data)) {
      this.partNumber = this.data.partNumber;
      set(this, 'opAmpStatus', this.data.status);
      set(this, 'packageCode', this.data.packageCode);
      this.vNoise = this.data.vNoise;
      this.iNoise = this.data.iNoise;
      set(this, 'minStableGain', this.data.minStableGain);
      this.vintage = this.data.vintage;
      this.vos = this.data.vos;
      this.spiceModels = this.data.spiceModels;
      this.polesZeroes = this.data.polesZeroes;
    }

    set(this, 'opAmpRecommended', {
      status: 'amp_status_recommended',
      comment: 'amp_status_recommended',
      commentComparer: 'comment_dropDown_recommended',
      selectable: true,
      recommendedFlag: true,
    });

    set(this, 'opAmpMarginal', {
      status: 'amp_status_marginal',
      comment: 'amp_status_not_recommended',
      commentComparer: 'comment_dropDown_notRecommended',
      selectable: true,
      recommendedFlag: false,
    });

    this.opAmpMarginalHasNumberChannelsError = {
      status: 'amp_status_marginal',
      comment: 'amp_status_not_recommended',
      commentComparer: 'comment_dropDown_notRecommended',
      selectable: true,
      recommendedFlag: false,
    };

    set(this, 'opAmpDisabled', {
      status: 'amp_status_disabled',
      comment: 'amp_status_not_allowed',
      commentComparer: 'comment_dropDown_notAllowed',
      selectable: false,
      recommendedFlag: true,
    });
  }

  @computed('data.statusText') get statusText() {
    return this.data ? this.data.statusText : undefined;
  }

  @computed('data.price') get price() {
    return this.data ? this.data.price : undefined;
  }

  @computed('data.iBias') get iBias() {
    return this.data ? Number(this.data.iBias) : Number.NaN;
  }

  @computed('data.minSupplyVoltage') get minSupplyVoltage() {
    return this.data ? Number(this.data.minSupplyVoltage) : Number.NaN;
  }

  @computed('data.maxSupplyVoltage') get maxSupplyVoltage() {
    return this.data ? Number(this.data.maxSupplyVoltage) : Number.NaN;
  }

  @computed('data.numberChannels') get numberChannels() {
    return this.data ? Number(this.data.numberChannels) : Number.NaN;
  }

  @computed('data.rOut') get rOut() {
    return this.data ? Number(this.data.rOut) : Number.NaN;
  }

  @computed('data.slewRate') get slewRate() {
    return this.data ? Number(this.data.slewRate) : Number.NaN;
  }

  @computed('data.inHeadroomPlus') get inHeadroomPlus() {
    return this.data ? Number(this.data.inHeadroomPlus) : Number.NaN;
  }

  @computed('data.outHeadroomPlus') get outHeadroomPlus() {
    return this.data ? Number(this.data.outHeadroomPlus) : Number.NaN;
  }

  @computed('data.inHeadroomMinus') get inHeadroomMinus() {
    return this.data ? Number(this.data.inHeadroomMinus) : Number.NaN;
  }

  @computed('data.outHeadroomMinus') get outHeadroomMinus() {
    return this.data ? Number(this.data.outHeadroomMinus) : Number.NaN;
  }

  @computed('data.supplyCurrent') get supplyCurrent() {
    return this.data ? Number(this.data.supplyCurrent) : Number.NaN;
  }

  @computed('data.architecture') get feedbackMethod() {
    var result = this.data ? (this.data.architecture.indexOf('Current Feedback') > -1 ? 'Current' : 'Voltage') : null;
    return result;
  }

  @computed(
    'componentsInput',
    'data',
    'feedbackMethod',
    'gbw',
    'iBias',
    'inHeadroomMinus',
    'inHeadroomPlus',
    'limits',
    'marginalLimits',
    'maxSupplyVoltage',
    'minStableGain',
    'minSupplyVoltage',
    'name',
    'numberChannels',
    'opAmpStatus',
    'outHeadroomMinus',
    'outHeadroomPlus',
    'packageCode',
    'rOut',
    'slewRate',
    'supplyCurrent',
  )
  get validation() {
    var limits = this.limits;
    var marginalLimits = this.marginalLimits;
    var componentsInput = this.marginalLimits;
    var opAmp = EmberObject.create({
      name: this.name,
      status: this.opAmpStatus,
      feedbackMethod: this.feedbackMethod,
      minStableGain: this.minStableGain,
      gbw: this.gbw,
      minSupplyVoltage: this.minSupplyVoltage,
      maxSupplyVoltage: this.maxSupplyVoltage,
      packageCode: this.packageCode,
      numberChannels: this.numberChannels,
      rOut: this.rOut,
      supplyCurrent: this.supplyCurrent,
      slewRate: this.slewRate,
      inHeadroomPlus: this.inHeadroomPlus,
      outHeadroomPlus: this.outHeadroomPlus,
      inHeadroomMinus: this.inHeadroomMinus,
      outHeadroomMinus: this.outHeadroomMinus,
      iBias: this.iBias,
    });
    var result = OpAmpValidation.create({
      opAmp: opAmp,
      limits: limits,
      marginalLimits: marginalLimits,
      componentsInput: componentsInput,
    });
    return result;
  }

  @computed(
    'opAmpDisabled',
    'opAmpMarginal',
    'opAmpRecommended',
    'validation.{numberChannels,opAmpMarginal,opAmpNotAllowed,whyNotTextArray}',
  )
  get opAmpState() {
    var opAmpNotAllowed = this.validation.opAmpNotAllowed;
    var opAmpMarginal = this.validation.opAmpMarginal;
    var whyNotTextArray = this.validation.whyNotTextArray;
    var numberChannels = this.validation.numberChannels;
    var result = this.opAmpRecommended;

    if (opAmpMarginal) {
      result = this.opAmpMarginal;
    }

    if (opAmpNotAllowed) {
      if (whyNotTextArray.length === 1 && numberChannels !== '') {
        result = this.opAmpMarginal;
      } else {
        result = this.opAmpDisabled;
      }
    }

    return result;
  }

  @computed('opAmpState.status') get status() {
    return this.opAmpState.status;
  }
  @computed('opAmpState.comment') get comment() {
    return this.opAmpState.comment;
  }
  @computed('opAmpState.commentComparer') get commentComparer() {
    return this.opAmpState.commentComparer;
  }
  @computed('opAmpState.selectable') get selectable() {
    return this.opAmpState.selectable;
  }
  @computed('opAmpState.recommendedFlag') get recommendedFlag() {
    return this.opAmpState.recommendedFlag;
  }
}
